import { useId, type ReactNode } from 'react'
import { PriorityBadge } from './PriorityBadge'
import type { ComparisonContext, DataProvenance, DecisionPriority } from './decisionSystem'

interface DecisionSummaryProps {
  /** The page's single decision question. Keep this stable while data changes. */
  question: string
  /** A plain-language conclusion, not merely a metric. */
  answer: ReactNode
  /** What changed, including direction and magnitude where available. */
  change?: ReactNode
  /** Revenue, margin, market-share, risk, or operational consequence. */
  impact?: ReactNode
  /** The most appropriate next step; normally a button or link with a verb label. */
  action?: ReactNode
  priority?: DecisionPriority
  comparison?: ComparisonContext
  provenance?: DataProvenance
  children?: ReactNode
  className?: string
}

/** Mandatory first-viewport sequence: question → answer → change → impact → action. */
export function DecisionSummary({
  question,
  answer,
  change,
  impact,
  action,
  priority = 'watch',
  comparison,
  provenance,
  children,
  className,
}: DecisionSummaryProps) {
  const questionId = useId()

  return (
    <section className={['decision-summary', className].filter(Boolean).join(' ')} data-decision-summary="" aria-labelledby={questionId}>
      <div className="decision-summary__content">
        <div className="decision-summary__eyebrow">
          <PriorityBadge priority={priority} />
          <span id={questionId}>{question}</span>
        </div>
        <h2 className="decision-summary__answer">{answer}</h2>
        {(change || impact) && (
          <div className="decision-summary__explanation">
            {change && <p><strong>What changed:</strong> {change}</p>}
            {impact && <p><strong>Why it matters:</strong> {impact}</p>}
          </div>
        )}
        {(comparison || provenance) && (
          <div className="decision-meta" aria-label="Data context">
            {comparison && <span>Compared with {comparison.baseline}{comparison.period ? ` · ${comparison.period}` : ''}</span>}
            {provenance?.updatedAt && <span>Updated {provenance.updatedAt}</span>}
            {provenance?.source && <span>Source: {provenance.source}</span>}
            {provenance?.confidence && <span>{provenance.confidence === 'unavailable' ? 'Confidence unavailable' : `${provenance.confidence} confidence`}</span>}
            {provenance?.isEstimate && <span>Estimate</span>}
          </div>
        )}
        {children}
      </div>
      {action && <div className="decision-summary__action"><span>Recommended next step</span>{action}</div>}
    </section>
  )
}
